"use client";

import React, { useState } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@wtc/ui";
import { useUpdateDeal } from "@/features/deals/hooks/use-deals-hooks";
import type { DealStage } from "@/lib/types/database/deals";
import type { ChangeDealStageModalData } from "@/lib/modals/data/modal-data-types";

import { ALL_STAGES } from "@/lib/constants/deal-stages";
import { getStageLabelForDisplay } from "@/lib/utils/deal/deal-lifecycle";

const DEAL_STAGES: DealStage[] = [...ALL_STAGES];

// This modal is mounted globally (app/layout.tsx → DetailPageModals), OUTSIDE
// any DetailActionProvider. It therefore CANNOT read DetailActionContext or
// UniversalEntityPageContext — those are always null/absent here. All inputs
// (the deal id + current stage) arrive through the global-modal-stack data
// payload supplied by the deal `changeStage` action handler. The write goes
// through useUpdateDeal(), which invalidates the cache (no onRefresh needed).
// See .claude/rules/GLOBAL_MODAL_NO_ACTION_CONTEXT.md + ACTION_MODAL_MOUNTS.md.
export function ChangeDealStageModal() {
  return (
    <ModalContainer<ChangeDealStageModalData> modalId={MODAL_IDS.deals.changeStage}>
      {({ isOpen, data, onClose }) => (
        <ChangeDealStageModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
        />
      )}
    </ModalContainer>
  );
}

interface ChangeDealStageModalContentProps {
  isOpen: boolean;
  data?: ChangeDealStageModalData;
  onClose: () => void;
}

function ChangeDealStageModalContent({
  isOpen,
  data,
  onClose,
}: ChangeDealStageModalContentProps) {
  const updateDealMutation = useUpdateDeal();

  const dealId = data?.dealId;
  const currentStage = data?.currentStage ?? "";

  const [selectedStage, setSelectedStage] = useState<DealStage | "">(
    (currentStage as DealStage) || ""
  );
  const [isLoading, setIsLoading] = useState(false);

  if (!dealId) return null;

  const handleChangeStage = async () => {
    if (!selectedStage || selectedStage === currentStage) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await updateDealMutation.mutateAsync({
        id: dealId,
        stage: selectedStage,
      });

      onClose();
    } catch (error) {
      console.error("Error changing deal stage:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.deals.changeStage}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Change Deal Stage"
      description="Select a new stage for this deal"
      maxWidth="sm"
    >
      <div className="p-4">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Current Stage: {currentStage}</label>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">New Stage</label>
            <Select value={selectedStage} onValueChange={(value) => setSelectedStage(value as DealStage)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a stage" />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {DEAL_STAGES.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {getStageLabelForDisplay(stage)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleChangeStage} disabled={isLoading || !selectedStage || selectedStage === currentStage}>
              {isLoading ? "Updating..." : "Update Stage"}
            </Button>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}
